import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { Settings, User, MapPin, PanelLeftIcon, Loader2 } from "lucide-react";
import { branchApiService, Branch } from "../services/branchApi";
import { NavigationSidebar } from "./navigation/NavigationSidebar";
import { ViewType, menuItems } from "./navigation/menuConfig";
import { toast } from "sonner";
import { useNavigate } from 'react-router-dom';
import { useBranch } from "../contexts/BranchContext";
import { useAuth } from "../contexts/AuthContext";
import { AppHeader } from './navigation/AppHeader';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const { selectedBranch, setSelectedBranch } = useBranch();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();

  const handleLogout = () => {
    setSelectedBranch(null);
    logout();
    navigate('/login');
  };

  // Determine active view based on current path
  const getActiveView = (): ViewType => {
    const path = location.pathname;
    
    if (path === '/dashboard') return ViewType.dashboard;
    if (path === '/endgoods') return ViewType.endgoodListing;
    if (path.startsWith('/processes')) return ViewType.processListing;
    if (path.startsWith('/hsn')) return ViewType.hsnListing;
    if (path.startsWith('/workers')) return ViewType.workerListing;
    if (path === '/raw-items/new') return ViewType.rawItemInformation;
    if (path === '/raw-items') return ViewType.rawItemListing;
    if (path === '/customers/new') return ViewType.customerInformation;
    if (path.startsWith('/customers/') && (path.endsWith('/edit') || path.endsWith('/view'))) return ViewType.customerInformation;
    if (path === '/customers') return ViewType.customerListing;
    if (path === '/suppliers/new') return ViewType.supplierInformation;
    if (path === '/suppliers') return ViewType.supplierListing;
    // if (path === '/purchase-orders/new') return ViewType.purchaseOrderForm;
    if (path === '/purchase-orders') return ViewType.purchaseOrderListing;

    if (path === '/sales-orders') return ViewType.salesOrderListing;
    if (path === '/production-planning') return ViewType.productionPlanning;
    if (path === '/bom') return ViewType.bomListing;
    if (path === '/wip-tracking') return ViewType.wipTracking;
    if (path === '/grn') return ViewType.grnListing;
    if (path === '/raw-material-receipt') return ViewType.rawMaterialReceipt;
    if (path === '/profile') return ViewType.userProfile;
    if (path === '/invoices/hsn-report') return ViewType.hsnReport;
    if (path === '/invoices/tracking') return ViewType.invoiceTracking;
    if (path === '/packing-entries') return ViewType.packingEntries;
    if (path.startsWith('/packing-entries/box-master')) return ViewType.boxMaster;

    return ViewType.dashboard;
  };

  // Fetch branches from API (legacy Layout load — header uses userBranches for selection)
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true);
        const fetchedBranches = await branchApiService.getBranches();
        setBranches(fetchedBranches);
      } catch (error) {
        toast.error('Failed to load branches');
        
        const fallbackBranches = [
          { id: 1, branchName: "Main Branch - Mumbai", branchCode: "MB", address: "Mumbai, Maharashtra", contactPerson: "Mumbai Admin", phone: "1234567890", email: "mumbai@company.com", isActive: true, gstin: "GST123456789", stateName: "Maharashtra", stateCode: "27", panNo: "PAN123456789", createdAt: "2025-01-01", updatedAt: "2025-01-01" },
          { id: 2, branchName: "Branch 2 - Delhi", branchCode: "DB", address: "Delhi, NCR", contactPerson: "Delhi Admin", phone: "0987654321", email: "delhi@company.com", isActive: true, gstin: "GST987654321", stateName: "Delhi", stateCode: "07", panNo: "PAN987654321", createdAt: "2025-01-01", updatedAt: "2025-01-01" },
          { id: 3, branchName: "Branch 3 - Bangalore", branchCode: "BB", address: "Bangalore, Karnataka", contactPerson: "Bangalore Admin", phone: "1122334455", email: "bangalore@company.com", isActive: true, gstin: "GST112233445", stateName: "Karnataka", stateCode: "29", panNo: "PAN987654321", createdAt: "2025-01-01", updatedAt: "2025-01-01" },
          { id: 4, branchName: "Branch 4 - Chennai", branchCode: "CB", address: "Chennai, Tamil Nadu", contactPerson: "Chennai Admin", phone: "5566778899", email: "chennai@company.com", isActive: true, gstin: "GST556677889", stateName: "Tamil Nadu", stateCode: "33", panNo: "PAN556677889", createdAt: "2025-01-01", updatedAt: "2025-01-01" }
        ];
        setBranches(fallbackBranches);
      } finally {
        setLoading(false);
      }
    };

    loadBranches();
  }, []);

  const getCurrentPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/packing-entries/box-master/endgoods')) {
      return 'Box Master — Endgood links';
    }
    if (path.startsWith('/processes')) {
      return 'Process Master';
    }
    const currentItem = menuItems.find((item) => item.path === path);
    return currentItem?.label || 'Dashboard';
  };

  const getCurrentPageDescription = () => {
    if (location.pathname === "/theme-comparison") {
      return "Compare different color themes for your dashboard";
    }
    return "Manage your manufacturing operations with style";
  };



  const activeView = getActiveView();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Left Sidebar */}
      <div className="flex-shrink-0 h-full">
        <NavigationSidebar 
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>
      
      {/* Right Content Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <AppHeader 
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activeView={activeView}
          onLogout={handleLogout}
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebarCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        
        {/* Main Content with its own scroll container */}
        <main className="flex-1 overflow-y-auto p-6 bg-background scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {children}
        </main>
      </div>
    </div>
  );
} 